import { ReportFormatType, NetworkProviderFilterType } from "@app/exports/enumerations";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { FC, useContext, useMemo } from "react";
import { ReportCustomizationReducerActions } from "@app/report-customization/build-reports/interfaces/reportCustomizationReducerActionsEnum";
import { ReportCustomizationContext } from "@app/report-customization/build-reports/ReportCustomizationContext";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { ReportCustomizationRunConfigurationNamesEnum } from "@app/report-customization/build-reports/interfaces/reportCustomizationRunConfigurationNamesEnum";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { getProviderFilterOptions, isFilterableNetworkType } from "@app/report-customization/build-reports/helpers/helpers";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

export const ProviderSection: FC = () => {
  const rcContext = useContext(ReportCustomizationContext);
  const user = useContext(UserContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);

  const restrictToNetwork = enabledFeatureFlags.has(Features.RPPO_NON_PAR_PROVIDER_TO_HSD_TABLE_INCLUSION)
    && !!rcContext.findTimeslice(rcContext.state.timeslice)?.rppoNonContractedProvidersIncludedInHsd
    && rcContext.state.reportFormat === ReportFormatType.MA;

  const { providerSourceOptions } = useMemo(() => {
    const distinctPrefix = rcContext.routeState.isZipProject ? "Distinct " : "";
    const runFrequencyTimeslices = rcContext.state.allTimeslices[rcContext.state.runFrequency];
    const currentRunConfig = rcContext.findTimeslice(rcContext.state.timeslice);
    const options = [];

    // Always add Network option (assumed always available)
    options.push(<option key="network" value={ProviderNetworkTypeEnum.Network}>{distinctPrefix}Network Servicing Providers</option>);

    // Prospect Providers
    if (runFrequencyTimeslices.some((t) => t.hasProspectProviders)) {
      let currentConfigHasProspect = false;
      if (currentRunConfig && 'hasProspectProviders' in currentRunConfig) {
        currentConfigHasProspect = currentRunConfig.hasProspectProviders;
      }
      options.push(
        <option
          key="prospect"
          disabled={rcContext.state.isCompareMode || rcContext.state.isDualMembership || !currentConfigHasProspect || restrictToNetwork}
          value={ProviderNetworkTypeEnum.Prospect}
        >
          {distinctPrefix}Prospect Servicing Providers
        </option>
      );
    }

    // Market Providers
    if (runFrequencyTimeslices.some((t) => t.hasMarketProviders)) {
      let currentConfigHasMarket = false;
      if (currentRunConfig && 'hasMarketProviders' in currentRunConfig) {
        currentConfigHasMarket = currentRunConfig.hasMarketProviders;
      }
      options.push(
        <option
          key="market"
          disabled={rcContext.state.isCompareMode || rcContext.state.isDualMembership || !currentConfigHasMarket || restrictToNetwork}
          value={ProviderNetworkTypeEnum.Market}
        >
          {distinctPrefix}Market Servicing Providers
        </option>
      );

    }

    // Next Closest Providers
    if (runFrequencyTimeslices.some((t) => t.showNextClosestProviders)) {
      const currentConfigHasNextClosest = !!currentRunConfig?.showNextClosestProviders;
      options.push(
        <option
          key="nextclosestproviders"
          disabled={rcContext.state.isCompareMode || rcContext.state.isDualMembership || !currentConfigHasNextClosest}
          value={ProviderNetworkTypeEnum.NextClosestProviders}
        >
          Next Closest Providers
        </option>
      );
    }

    // Net New Providers
    if (rcContext.state.showNetNewProviders && user.roles.includes(Roles.NET_NEW_PROVIDER_USER)) {

      const shouldShowNetNew = currentRunConfig && (
        currentRunConfig.name === ReportCustomizationRunConfigurationNamesEnum.LatestPublished
        || currentRunConfig.name === ReportCustomizationRunConfigurationNamesEnum.Unpublished
        || currentRunConfig.name === ReportCustomizationRunConfigurationNamesEnum.Projected
        || currentRunConfig.name === ReportCustomizationRunConfigurationNamesEnum.Termed
        || currentRunConfig.name === ReportCustomizationRunConfigurationNamesEnum.ProjectedTermed
      );
      options.push(
        <option
          key="netnewproviders"
          disabled={rcContext.state.isCompareMode || rcContext.state.isDualMembership || !shouldShowNetNew}
          value={ProviderNetworkTypeEnum.NetNew}
        >
          Net New Providers
        </option>
      );
    }

    return { providerSourceOptions: options };
  }, [
    rcContext.state.isCompareMode,
    rcContext.state.selectedMemberships,
    rcContext.state.allTimeslices,
    rcContext.state.timeslice,
    rcContext.state.showNetNewProviders,
    restrictToNetwork
  ]);

  const providerFilterOptions = useMemo(() => {
    const filters = getProviderFilterOptions(rcContext.state.reportType, rcContext.state.providerSource, enabledFeatureFlags);
    return filters.map((f) => <option key={f.key} value={f.key}>{f.value}</option>);
  }, [rcContext.state.providerSource, rcContext.state.reportType, enabledFeatureFlags]);

  const isPocDisabled = rcContext.state.providerSource !== ProviderNetworkTypeEnum.Network || (rcContext.state.reportFormat !== ReportFormatType.MA && rcContext.state.reportFormat !== ReportFormatType.MMP);
  const showProviderFilterOpton = isFilterableNetworkType(rcContext.state.providerSource);
  const pocTooltip = enabledFeatureFlags.has(Features.SHOW_MMP_HSD_FORMAT)
    ? "Select Network Providers in the Provider Filter and MA HSD or MMP HSD Format to Append Pattern of Care Providers"
    : "Select Network Providers in the Provider Filter and MA HSD Format to Append Pattern of Care Providers";

  return (
    <>
      <div className="row mt-3">
        <h2 className="header-s col-auto">Provider</h2>
      </div>
      <div className="row">
        <div className="col-4">
          <TooltipWrapper
            containerClassName="d-block"
            triggerWidth="100%"
            text={restrictToNetwork ? "This option is available only for Network provider sets." : ""}
          >
            <QESSelect
              label="Provider Source"
              value={rcContext.state.providerSource}
              onChange={(e) => rcContext.changeProviderSource(e.target.value as ProviderNetworkTypeEnum)}
              testId="report-customization__provider-source"
            >
              {providerSourceOptions}
            </QESSelect>
          </TooltipWrapper>
        </div>
      </div>

      {
        !!rcContext.findTimeslice(rcContext.state.timeslice)?.showNextClosestProviders
        && rcContext.state.providerSource === ProviderNetworkTypeEnum.Network
        // we want this hidden on all zip project reports, but only this one shows the ProviderSection
        && rcContext.state.reportType !== ReportType.ZipAnalysisAdequacyServicingProvider
        && (
          <QESCheckbox
            label="Append Next Closest Network Providers"
            containerClassName="ms-1"
            checked={rcContext.state.appendNextClosestProviders}
            onChange={(e) => rcContext.dispatch({ payload: { appendNextClosestProviders: e.target.checked }, type: ReportCustomizationReducerActions.ChangeAppendNextClosestProviders })}
            testId="report-customization__append-ncp"
            disabled={rcContext.state.isCompareMode}
          />
        )
      }

      {
        !rcContext.routeState.isZipProject
        && user.roles.includes(Roles.PATTERN_OF_CARE_USER)
        && rcContext.routeState.showPatternOfCare
        && (
          <div className="ms-1 my-2">
            <TooltipWrapper
              containerClassName="report-customization__append-poc"
              text={isPocDisabled
                ? pocTooltip : ""
              }
            >
              <QESCheckbox
                label="Append Pattern of Care Providers"
                containerClassName="my-0"
                checked={rcContext.state.appendPatternOfCareProviders}
                onChange={(e) => rcContext.dispatch({ payload: { appendPatternOfCareProviders: e.target.checked }, type: ReportCustomizationReducerActions.ChangeAppendPatternOfCare })}
                testId="report-customization__append-poc"
                disabled={isPocDisabled}
              />
            </TooltipWrapper>
          </div>
        )
      }

      {
        showProviderFilterOpton && (
          <div className="row mt-2">
            <QESSelect
              label="Provider Filter"
              containerClassName="col-4"
              value={rcContext.state.providerFilter}
              onChange={(e) => rcContext.dispatch({ payload: { providerFilter: e.target.value as NetworkProviderFilterType }, type: ReportCustomizationReducerActions.ChangeProviderFilter })}
              testId="report-customization__provider-filter"
            >
              {providerFilterOptions}
            </QESSelect>
          </div>
        )
      }
      <div className="row mb-2">

        {((!!rcContext.state.timeslice && !!rcContext.state.comparedTimeslice) || (rcContext.state.selectedMemberships.length === 2)) && rcContext.state.providerSource === ProviderNetworkTypeEnum.Network && (
          <div className="col-auto">
            <QESCheckbox
              label="Added Providers"
              containerClassName="ms-1"
              checked={rcContext.state.showAddedProviders}
              onChange={(e) => rcContext.dispatch({ payload: { showAddedProviders: e.target.checked }, type: ReportCustomizationReducerActions.ChangeShowAddedProviders })}
              testId="report-customization__added-providers"
            />
            <QESCheckbox
              label="Matched Providers"
              containerClassName="ms-1"
              checked={rcContext.state.showMatchedProviders}
              onChange={(e) => rcContext.dispatch({ payload: { showMatchedProviders: e.target.checked }, type: ReportCustomizationReducerActions.ChangeShowMatchedProviders })}
              testId="report-customization__matched-providers"
            />
            <QESCheckbox
              label="Removed Providers"
              containerClassName="ms-1"
              checked={rcContext.state.showRemovedProviders}
              onChange={(e) => rcContext.dispatch({ payload: { showRemovedProviders: e.target.checked }, type: ReportCustomizationReducerActions.ChangeShowRemovedProviders })}
              testId="report-customization__removed-providers"
            />
          </div>
        )}
      </div >
    </>
  )
}